export const TAX_PENDING_MESSAGE =
  'Tax will be calculated and applied prior to completing purchase.'

/**
 * @param {number | string | null | undefined} percent
 */
export function formatDiscountPercentForDisplay(percent) {
  const p = Number(percent)
  if (!Number.isFinite(p) || p <= 0) return ''
  const display = p > 0 && p <= 1 ? p * 100 : p
  if (Number.isInteger(display)) return String(display)
  return display.toFixed(2).replace(/\.?0+$/, '')
}

/**
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function discountAmountFromDocument(doc) {
  if (!doc || typeof doc !== 'object') return 0
  const raw = doc.discountAmount
  if (raw == null || raw === '') return 0
  const amount = Number(raw)
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

/**
 * @param {Record<string, unknown> | null | undefined} doc
 * @returns {{ amount: number, percent: number | null } | null}
 */
export function resolveBillingDocumentDiscount(doc) {
  if (!doc || typeof doc !== 'object') return null
  const amount = discountAmountFromDocument(doc)
  if (amount <= 0) return null

  const rawPercent = doc.discountPercent
  let percent = null
  if (rawPercent != null && rawPercent !== '') {
    const p = Number(rawPercent)
    if (Number.isFinite(p) && p > 0) percent = p
  }

  return { amount, percent }
}

/**
 * @param {{ amount: number, percent?: number | null, scope?: string | null } | null | undefined} discount
 */
export function formatDiscountLabel(discount) {
  if (!discount) return ''
  const seatScoped = discount.scope === 'seats'
  if (discount.percent != null) {
    const pctStr = formatDiscountPercentForDisplay(discount.percent)
    if (pctStr) {
      return seatScoped
        ? `Seat License Discount (${pctStr}%):`
        : `Discount (${pctStr}%):`
    }
  }
  return seatScoped ? 'Seat License Discount:' : 'Discount:'
}

/**
 * @param {{ amount: number } | null | undefined} discount
 */
export function formatDiscountAmountDisplay(discount) {
  if (!discount) return ''
  return `−$${Number(discount.amount).toFixed(2)}`
}

/**
 * @param {boolean} orgTaxExempt
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function isTaxExemptForDisplay(orgTaxExempt, doc) {
  return Boolean(orgTaxExempt || doc?.isTaxExempt)
}

/**
 * Whether tax/total on a billing document came from QuickBooks (or backend sync).
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function billingDocumentTaxResolved(doc) {
  if (!doc) return false
  if (doc.isTaxExempt) return true
  if (doc.quickbooksEstimateId || doc.quickbooksInvoiceId) return true
  const tax = doc.taxAmount
  const total = doc.total
  if (tax != null && tax !== '' && total != null && total !== '') {
    const taxNum = Number(tax)
    const totalNum = Number(total)
    if (!Number.isNaN(taxNum) && !Number.isNaN(totalNum)) return true
  }
  return false
}

/**
 * Totals for cart and saved billing document UI (display only).
 * @param {number} subtotal pre-discount subtotal
 * @param {boolean} orgTaxExempt org has an Active approved tax-exempt certificate
 * @param {Record<string, unknown> | null | undefined} [doc] saved billing document
 * @param {{ amount: number, percent?: number | null } | null | undefined} [cartDiscount]
 *        explicit cart discount when there is no saved document
 */
export function resolvePortalTotals(
  subtotal,
  orgTaxExempt,
  doc = null,
  cartDiscount = null
) {
  const sub = Number(subtotal) || 0

  if (doc) {
    const docSub = Number(doc.subtotal) || sub
    const discountAmt = discountAmountFromDocument(doc)
    const afterDiscount = Math.max(0, docSub - discountAmt)
    const discount = resolveBillingDocumentDiscount(doc)
    const exempt = isTaxExemptForDisplay(orgTaxExempt, doc)

    if (exempt) {
      return {
        subtotal: docSub,
        discount,
        taxAmount: 0,
        total: afterDiscount,
        taxPending: false,
        isTaxExempt: true,
      }
    }

    if (billingDocumentTaxResolved(doc)) {
      const tax = Number(doc.taxAmount) || 0
      return {
        subtotal: docSub,
        discount,
        taxAmount: tax,
        total: afterDiscount + tax,
        taxPending: false,
        isTaxExempt: false,
      }
    }

    return {
      subtotal: docSub,
      discount,
      taxAmount: null,
      total: afterDiscount,
      taxPending: true,
      isTaxExempt: false,
    }
  }

  const discount =
    cartDiscount && Number(cartDiscount.amount) > 0
      ? {
          amount: Number(cartDiscount.amount),
          percent:
            cartDiscount.percent != null && Number(cartDiscount.percent) > 0
              ? Number(cartDiscount.percent)
              : null,
          scope: cartDiscount.scope || null,
        }
      : null
  const afterDiscount = Math.max(0, sub - (discount?.amount || 0))

  if (orgTaxExempt) {
    return {
      subtotal: sub,
      discount,
      taxAmount: 0,
      total: afterDiscount,
      taxPending: false,
      isTaxExempt: true,
    }
  }

  return {
    subtotal: sub,
    discount,
    taxAmount: null,
    total: afterDiscount,
    taxPending: true,
    isTaxExempt: false,
  }
}

/**
 * @param {{ isTaxExempt?: boolean, taxPending?: boolean, taxAmount?: number | null }} t
 */
export function formatTaxDisplay(t) {
  if (t.isTaxExempt) return 'EXEMPT'
  if (t.taxPending) return TAX_PENDING_MESSAGE
  return `$${Number(t.taxAmount || 0).toFixed(2)}`
}

/**
 * @param {{ isTaxExempt?: boolean, taxPending?: boolean, subtotal?: number, total?: number | null }} t
 */
export function formatTotalDisplay(t) {
  const resolvedTotal =
    t.total != null && t.total !== ''
      ? Number(t.total)
      : Number(t.subtotal) || 0

  if (t.isTaxExempt) {
    return `$${resolvedTotal.toFixed(2)}`
  }
  if (t.taxPending) {
    if (t.total == null) return 'TBD'
    return `$${Number(t.total).toFixed(2)}`
  }
  return `$${resolvedTotal.toFixed(2)}`
}

/**
 * @param {{ isTaxExempt?: boolean, taxPending?: boolean, subtotal?: number, total?: number | null }} t
 */
export function payableAmount(t) {
  if (t.total != null && t.total !== '') return Number(t.total) || 0
  return Number(t.subtotal) || 0
}
